import React from 'react';
import { View, StyleSheet, StyleProp, ViewStyle } from 'react-native';
import { Text } from '@/components/common/text';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { useTypography } from '@/hooks/shared/useTypography';
import { useFontScale } from '@/hooks/shared/useFontScale';

interface GreetingSectionProps {
  greeting: string;
  firstName: string;
  unitName: string;
  hasImage?: boolean;
  /**
   * Steps the greeting down from heading1 to heading2 — for the phone's
   * no-scroll home, where a 28pt title is height the planner does not get.
   * Off by default (desktop keeps the full size).
   */
  dense?: boolean;
  style?: StyleProp<ViewStyle>;
}

export const GreetingSection: React.FC<GreetingSectionProps> = React.memo(({
  greeting,
  firstName,
  unitName,
  hasImage = false,
  dense = false,
  style,
}) => {
  const { colors } = useThemeColors();
  const type = useTypography();
  const { isLargeText } = useFontScale();

  // One line at normal sizes: the no-scroll home hands every point above the
  // planner to the todo list, so the unit sits at the trailing end of the
  // greeting's own line, on its baseline. The title is the part that yields —
  // it ellipsizes — so a long name can never push the unit off or wrap the
  // row. At large text (isLargeText) the row cannot hold both, so it stacks
  // instead and the title may take two lines.
  return (
    <View style={[styles.container, isLargeText && styles.containerStacked, style]}>
      <Text
        style={[
          styles.title,
          dense ? type.heading2 : type.heading1,
          { color: colors.text.primary },
        ]}
        numberOfLines={isLargeText ? 2 : 1}
        ellipsizeMode="tail"
      >
        {greeting}, {firstName}
      </Text>
      {unitName ? (
        <Text
          style={[
            styles.unit,
            type.body,
            { color: colors.text.dim },
            hasImage && { color: colors.text.imageSubtitle },
          ]}
          numberOfLines={1}
        >
          {unitName}
        </Text>
      ) : null}
    </View>
  );
});

GreetingSection.displayName = 'GreetingSection';

const styles = StyleSheet.create({
  container: {
    paddingTop: 24,
    marginBottom: 16,
    flexDirection: 'row',
    // Baseline, not center: a 14pt label centered against a 28pt heading
    // floats mid-glyph; on the baseline it reads as part of the same line.
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: 12,
  },
  // At large text the row cannot hold a 1.5x heading and the unit side by
  // side, so the unit drops under a title that may take two lines.
  containerStacked: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 4,
  },
  // Type comes from useTypography() at render (heading1 or heading2, body);
  // these carry only the row behavior.
  title: {
    flexShrink: 1,
  },
  unit: {
    flexShrink: 0,
  },
});
